import type { TFunction } from 'i18next';

import {
  ScreenshotStatus,
  CreativeFlag,
  getScreenshotCampaigns,
  getCreatives,
} from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { usePollingInterval } from '@/js/libs/hooks/usePollingInterval';
import { isDebug } from '@/js/libs/is-debug';
import { useSubscribe } from '@/js/libs/observable';
import { Icons } from '@/js/libs/ui/src/V2';
import { useIdentityRoles, useRolesMatch } from '@/js/libs/use-auth';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { ROUTER } from 'src/AppRouter.tsx';
import { useRecover } from 'src/hooks/useRecover.tsx';
import { INVOICING_PAGE_ROLES } from '../invoicingAccess.ts';

export const useSelfBookingNavItems = (t: TFunction) => {
  const { ADMIN: isAdmin, OPS_CHECKS: isOpsChecks, OPS_SCREENSHOTS: isOpsScreenshots, YALEO_SELF_BOOKING } = useIdentityRoles();
  const canOpenInvoicing = useRolesMatch({ roles: INVOICING_PAGE_ROLES });

  const selfBookingCreativesApprovalListRequest = useRequest({
    apiMethod: getCreatives,
    params: YALEO_SELF_BOOKING && (isAdmin || isOpsChecks) ? { flag: CreativeFlag.PENDING_APPROVAL } : NotActive,
    silent: true,
  });

  const selfBookingScreenshotCampaignsRequest = useRequest({
    apiMethod: getScreenshotCampaigns,
    params: YALEO_SELF_BOOKING && (isAdmin || isOpsScreenshots) ? { status: ScreenshotStatus.NEW } : NotActive,
    silent: true,
  });

  usePollingInterval(
    () => {
      selfBookingCreativesApprovalListRequest.retry();
      selfBookingScreenshotCampaignsRequest.retry();
    },
    { intervalMs: 60_000 },
  );

  const creativesApprovalCount = useSubscribe(selfBookingCreativesApprovalListRequest.data$, (response) => response?.data.total ?? 0);
  const screenshotCampaignsCount = useSubscribe(selfBookingScreenshotCampaignsRequest.data$, (response) => response?.data.total ?? 0);

  const creativesApprovalRecover = useRecover(selfBookingCreativesApprovalListRequest);
  const screenshotCampaignsRecover = useRecover(selfBookingScreenshotCampaignsRequest);

  const items = [
    {
      icon: <Icons.Dashboard />,
      title: t('common:DASHBOARD'),
      to: ROUTER['/self-booking/dashboard'].path.pattern,
      path: ROUTER['/self-booking/dashboard'].path.pattern,
      dataTestId: 'self-booking-dashboard-link',
      hidden: !isDebug(),
    },
    {
      icon: <Icons.Document />,
      title: t('common:CAMPAIGNS'),
      to: ROUTER['/self-booking/campaigns'].path.pattern,
      path: ROUTER['/self-booking/campaigns'].path.pattern,
      dataTestId: 'self-booking-campaigns-link',
    },
    {
      icon: <Icons.List />,
      title: t('common:POSITIONS'),
      to: ROUTER['/self-booking/positions'].path.pattern,
      path: ROUTER['/self-booking/positions'].path.pattern,
      dataTestId: 'self-booking-positions-link',
    },
    {
      icon: <Icons.Users />,
      title: t('common:ADVERTISERS'),
      to: ROUTER['/self-booking/advertisers'].path.pattern,
      path: ROUTER['/self-booking/advertisers'].path.pattern,
      dataTestId: 'self-booking-advertisers-link',
    },
    {
      icon: <Icons.Photo />,
      title: t('common:CREATIVES'),
      to: ROUTER['/self-booking/creatives'].path.pattern,
      path: ROUTER['/self-booking/creatives'].path.pattern,
      dataTestId: 'self-booking-creatives-link',
    },
    {
      icon: <Icons.Check />,
      title: t('common:CREATIVE_APPROVAL'),
      to: ROUTER['/self-booking/creatives-approval'].path.pattern,
      path: ROUTER['/self-booking/creatives-approval'].path.pattern,
      dataTestId: 'self-booking-creatives-approval-link',
      hidden: !isAdmin && !isOpsChecks,
      showIndicator: creativesApprovalCount > 0,
      tag: creativesApprovalRecover,
    },
    {
      icon: <Icons.Display />,
      title: t('components:PN_SCREENSHOT_MANAGEMENT'),
      to: ROUTER['/self-booking/screenshot-management'].path.pattern,
      path: ROUTER['/self-booking/screenshot-management'].path.pattern,
      dataTestId: 'self-booking-screenshot-management-link',
      hidden: !isAdmin && !isOpsScreenshots,
      showIndicator: screenshotCampaignsCount > 0,
      tag: screenshotCampaignsRecover,
    },
    {
      icon: <Icons.Dollar />,
      title: t('components:INVOICING'),
      to: ROUTER['/self-booking/invoicing'].path.pattern,
      path: ROUTER['/self-booking/invoicing'].path.pattern,
      dataTestId: 'self-booking-invoicing-link',
      hidden: !canOpenInvoicing,
    },
    {
      icon: <Icons.Draft />,
      title: t('components:RECEIPTS'),
      to: ROUTER['/self-booking/receipts/payments'].path.pattern,
      path: ROUTER['/self-booking/receipts'].path.pattern,
      dataTestId: 'self-booking-receipts-payments-link',
    },
    {
      icon: <Icons.Revenue />,
      title: t('common:PAYMENT_METHODS'),
      to: ROUTER['/self-booking/payment-methods'].path.pattern,
      path: ROUTER['/self-booking/payment-methods'].path.pattern,
      dataTestId: 'self-booking-payment-methods-link',
    },
  ].filter((item) => !item.hidden);

  const showIndicator =
    !creativesApprovalRecover && !screenshotCampaignsRecover && (creativesApprovalCount > 0 || screenshotCampaignsCount > 0);

  return { items, showIndicator };
};
